import { Button } from '@berky/ui'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Link } from 'react-router'

interface Props {
  moduleId: string
  moduleTitle: string
  /** Kurzform für kleine Flächen wie eine Kachel auf der Startseite. */
  compact?: boolean
  /** Passt Nutzertexte für eine ausdrücklich registrierte Plattformfunktion an. */
  platformFeature?: boolean
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Fehlerisolierung pro Modul.
 *
 * Stürzt ein Modul ab, bleibt der Rest des Portals bedienbar: Navigation,
 * Startseite und alle anderen Module laufen weiter. Das ist die wichtigste
 * Schutzmaßnahme der Plattform — ein fehlerhaftes Modul darf niemals das
 * ganze Portal lahmlegen.
 */
export class ModuleErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Später zusätzlich an ein Log-Backend schicken. Die Modul-ID gehört
    // unbedingt dazu, sonst weiß niemand, wer den Fehler verursacht hat.
    console.error(`[berky] Modul "${this.props.moduleId}" ist abgestürzt.`, error, info)
  }

  private reset = () => {
    this.setState({ error: null })
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    const chunkFailed = isChunkLoadError(error)

    if (this.props.compact) {
      return (
        <p role="alert" className="text-sm text-destructive-strong">
          Diese Kachel konnte nicht geladen werden. Die Anwendung selbst lässt sich
          weiterhin öffnen.
        </p>
      )
    }

    return (
      <div
        role="alert"
        className="mx-auto max-w-2xl rounded-lg border border-destructive/30 bg-destructive-surface p-6"
      >
        <h1 className="text-lg font-semibold text-destructive-strong">
          {chunkFailed
            ? this.props.platformFeature
              ? 'Diese Plattformfunktion konnte nicht geladen werden'
              : 'Diese Anwendung konnte nicht geladen werden'
            : this.props.platformFeature
              ? `In der Plattformfunktion „${this.props.moduleTitle}" ist ein Fehler aufgetreten`
              : `In der Anwendung „${this.props.moduleTitle}" ist ein Fehler aufgetreten`}
        </h1>

        <p className="mt-2 text-sm text-foreground">
          {chunkFailed
            ? 'Vermutlich wurde das Portal gerade aktualisiert, während Sie es geöffnet hatten. Laden Sie die Seite neu.'
            : 'Der Rest des Portals funktioniert weiter. Sie können es erneut versuchen oder zur Startseite zurückkehren.'}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {chunkFailed ? (
            <Button onClick={() => window.location.reload()}>Seite neu laden</Button>
          ) : (
            <Button onClick={this.reset}>Erneut versuchen</Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/">Zur Startseite</Link>
          </Button>
        </div>

        <details className="mt-4 text-sm text-muted-foreground">
          <summary className="cursor-pointer select-none">
            Technische Details für den Support
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-card p-3 text-xs">
            {this.props.moduleId}: {error.message}
          </pre>
        </details>
      </div>
    )
  }
}

/**
 * Erkennt fehlgeschlagene Lazy-Chunks. Typischer Fall: nach einem Deploy sind
 * die alten Dateinamen mit ihren Hashes verschwunden, der Browser hat aber noch
 * die alte index.html.
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase()
  return (
    error.name === 'ChunkLoadError' ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to fetch dynamically')
  )
}
